import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Calendar, User, Award, FileText, Eye } from 'lucide-react';
import { reportesJugadoresService } from '../../services/reportesJugadoresService';
import type { ReporteJugador } from '../../types/reportesJugadores';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { translatePosition } from '../../utils/positionTranslations';

const ReportesJugadoresListPage: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const currentLang = i18n.language as 'es' | 'en';
  const [reportes, setReportes] = useState<ReporteJugador[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadReportes();
  }, [page, search]);

  const loadReportes = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await reportesJugadoresService.getReportes(page, 20, search);

      if (response.success && response.data) {
        setReportes(response.data.data);
        setTotalPages(response.data.pagination.total_pages);
        setTotal(response.data.pagination.total);
      } else {
        setError(response.error || 'Error al cargar los reportes');
      }
    } catch (err) {
      setError('Error de conexión al cargar los reportes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1); // Reset to first page on search
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getNotaColor = (nota: number | null | undefined): string => {
    if (!nota) return 'text-gray-400';
    if (nota >= 8) return 'text-green-600';
    if (nota >= 6) return 'text-yellow-600';
    if (nota >= 4) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Reportes de <span className="text-blue-600">Jugadores</span>
          </h1>
          <p className="text-gray-600">
            Gestiona tus reportes de scouting
          </p>
        </div>

        <button
          onClick={() => navigate('/sporting/reportes-jugadores/new')}
          className="btn-primary flex items-center gap-2 self-start lg:self-auto"
        >
          <Plus className="w-4 h-4" />
          Nuevo Reporte
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Buscar por jugador o equipo rival..."
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{total}</p>
              <p className="text-sm text-gray-600">Reportes Totales</p>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Reports List */}
      {!loading && reportes.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No hay reportes
          </h3>
          <p className="text-gray-600 mb-6">
            {search ? 'No se encontraron reportes con ese criterio' : 'Comienza creando tu primer reporte de jugador'}
          </p>
          <button
            onClick={() => navigate('/sporting/reportes-jugadores/new')}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Crear Primer Reporte
          </button>
        </div>
      )}

      {!loading && reportes.length > 0 && (
        <div className="grid grid-cols-1 gap-6">
          {reportes.map((reporte) => (
            <div
              key={reporte.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:border-blue-500 transition-all duration-300 cursor-pointer group"
              onClick={() => navigate(`/sporting/reportes-jugadores/${reporte.id}`)}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                {/* Left Section - Player Info */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {reporte.player_name || 'Jugador no encontrado'}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-600">
                        {reporte.posicion_principal && (
                          <span className="flex items-center gap-1">
                            <Award className="w-4 h-4" />
                            {translatePosition(reporte.posicion_principal, currentLang)}
                          </span>
                        )}
                        {reporte.pie_preferido && (
                          <span>• {reporte.pie_preferido}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Match Info */}
                  {(reporte.nombre_competicion || reporte.nombre_equipo_rival) && (
                    <div className="pl-16 space-y-2 text-sm text-gray-700">
                      {reporte.nombre_competicion && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span>{reporte.nombre_competicion}</span>
                          {reporte.jornada && <span>• Jornada {reporte.jornada}</span>}
                        </div>
                      )}
                      {reporte.nombre_equipo_rival && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">vs</span>
                          <span className="font-medium">{reporte.nombre_equipo_rival}</span>
                          {reporte.lugar_partido && (
                            <span className="text-gray-500">({reporte.lugar_partido})</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Date */}
                  <div className="pl-16 text-sm text-gray-600">
                    Informe: {formatDate(reporte.fecha_informe)}
                    {reporte.fecha_partido && ` • Partido: ${formatDate(reporte.fecha_partido)}`}
                  </div>
                </div>

                {/* Right Section - Ratings */}
                <div className="flex items-center gap-6 lg:border-l lg:border-gray-200 lg:pl-6">
                  {/* Nota General */}
                  {reporte.nota_general !== null && reporte.nota_general !== undefined && (
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">General</p>
                      <p className={`text-2xl font-bold ${getNotaColor(reporte.nota_general)}`}>
                        {reporte.nota_general.toFixed(1)}
                      </p>
                    </div>
                  )}

                  {/* Nota Ofensiva */}
                  {reporte.nota_ofensiva !== null && reporte.nota_ofensiva !== undefined && (
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Ofensiva</p>
                      <p className={`text-2xl font-bold ${getNotaColor(reporte.nota_ofensiva)}`}>
                        {reporte.nota_ofensiva.toFixed(1)}
                      </p>
                    </div>
                  )}

                  {/* Nota Defensiva */}
                  {reporte.nota_defensiva !== null && reporte.nota_defensiva !== undefined && (
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Defensiva</p>
                      <p className={`text-2xl font-bold ${getNotaColor(reporte.nota_defensiva)}`}>
                        {reporte.nota_defensiva.toFixed(1)}
                      </p>
                    </div>
                  )}

                  {/* View Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/sporting/reportes-jugadores/${reporte.id}`);
                    }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Ver
                  </button>
                </div>
              </div>

              {/* Perfil (if exists) */}
              {reporte.perfil_estilo_juego && (
                <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-700 line-clamp-2">
                  {reporte.perfil_estilo_juego}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <span className="px-4 py-2 bg-gray-100 rounded-xl text-gray-900 font-medium">
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportesJugadoresListPage;
